/**
 * PostHog initializer — Phase 178.6.I Commit F.
 *
 * Wraps `posthog-js` with the privacy doctrine the user codified:
 *
 *   • DEFAULT OFF in production until privacy review passes
 *   • OPERATOR-ONLY initially — replay only fires for users with an
 *     operator_role (organizer / venue_owner / academy_owner / super_admin)
 *   • DENY-LIST masking — never rely on allow-list; suppress every
 *     surface that could contain:
 *       passwords · OTPs · payment inputs · Aadhaar / PAN · chats ·
 *       student sensitive data · financial values
 *   • SAFE BY DEFAULT — if init fails, all paths noop
 *
 * Env vars
 * ────────
 *   REACT_APP_POSTHOG_KEY                — public project key (required)
 *   REACT_APP_POSTHOG_HOST               — defaults to https://us.i.posthog.com
 *   REACT_APP_POSTHOG_SESSION_REPLAY     — set to "true" to enable replay
 *                                          (default off — privacy gate)
 *   REACT_APP_POSTHOG_AUTOCAPTURE        — set to "false" to disable
 *                                          autocapture (we use track() instead)
 */

// CSS class names that must NEVER be captured by session replay.
// Add these via `className="ph-no-capture"` on any element holding
// sensitive content.  Examples wired in this codebase:
//   • Payment forms (Razorpay), OTP inputs, password inputs, chat
//     compose box, student profile sections, financial dashboards.
export const PRIVACY_MASK_CLASS = 'ph-no-capture';

// Selectors that mask the entire ELEMENT TREE — not just text.
// `posthog-js` supports both `maskAllInputs` (default ON) and
// `maskTextSelector` (custom CSS selector).  We layer them.
const MASK_TEXT_SELECTORS = [
  // Universal — anything explicitly marked
  '.ph-no-capture',
  '.ph-mask-text',
  // Payment & financial inputs
  'input[type="password"]',
  'input[name*="otp" i]',
  'input[name*="card" i]',
  'input[name*="cvv" i]',
  'input[name*="aadhaar" i]',
  'input[name*="aadhar" i]',
  'input[name*="pan" i]',
  'input[name*="upi" i]',
  'input[autocomplete="cc-number"]',
  'input[autocomplete="cc-exp"]',
  'input[autocomplete="cc-csc"]',
  'input[autocomplete="one-time-code"]',
  // Chat & messaging surfaces
  '[data-testid*="chat-"]',
  '[data-testid*="message-"]',
  '[data-testid*="dm-"]',
  '[contenteditable="true"]',
  // Student / minor sensitive surfaces
  '[data-testid*="student-"]',
  '[data-testid*="minor-"]',
  '[data-testid*="guardian-"]',
  // Razorpay's injected UI
  '.razorpay-checkout-frame',
  'iframe[src*="razorpay"]',
].join(', ');


let _initPromise = null;
let _phInstance = null;

/**
 * Lazy-init PostHog.  Returns the SDK instance when ready, or null when
 * not configured / init failed.  Safe to call multiple times — only the
 * first call performs work.
 */
export async function initPostHog(currentUser = null) {
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    const key = process.env.REACT_APP_POSTHOG_KEY;
    if (!key || key === 'placeholder' || typeof window === 'undefined') {
      return null;
    }

    try {
      const mod = await import('posthog-js');
      const posthog = mod.default || mod.posthog || mod;

      const sessionReplayEnabled =
        (process.env.REACT_APP_POSTHOG_SESSION_REPLAY || '').toLowerCase() === 'true';
      const autocaptureEnabled =
        (process.env.REACT_APP_POSTHOG_AUTOCAPTURE || 'true').toLowerCase() !== 'false';

      // Operator-only gate: only enable replay for actual operators.
      // Players (role=player / student) NEVER get session replay even
      // if the env flag is on.
      const operatorRoles = new Set([
        'super_admin', 'venue_owner', 'academy_owner', 'organizer',
        'host', 'coach', 'admin',
      ]);
      const role = (currentUser?.role || currentUser?.operator_role || '').toLowerCase();
      const isOperator = operatorRoles.has(role);
      const replayShouldBoot = sessionReplayEnabled && isOperator;

      posthog.init(key, {
        api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',

        // Capture configuration
        autocapture: autocaptureEnabled,
        capture_pageview: true,
        capture_pageleave: true,

        // ── Privacy masking (deny-list doctrine) ─────────────────
        mask_all_text: false,           // we use selector-based masking
        mask_all_element_attributes: false,
        // posthog-js v1.x option name
        mask_text_selector: MASK_TEXT_SELECTORS,
        // posthog-js v2.x+ option name (forward-compat — extra key is ignored if not supported)
        mask_text_class: 'ph-mask-text',

        session_recording: {
          // ALL inputs masked unless explicitly opted in via class
          // `ph-include-input`.  This is the deny-list spec.
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
            email: false,        // operator emails are fine
            number: true,        // amounts / payment values
            search: false,
            tel: true,           // phone numbers
            text: false,         // most safe; selectors above mask the sensitive ones
            url: false,
            color: false,
            date: false,
            'datetime-local': false,
            month: false,
            range: false,
            time: false,
            week: false,
          },
          // Mask text on any element matching our deny-list selectors
          maskTextSelector: MASK_TEXT_SELECTORS,
          // Block whole sub-trees outright (no DOM serialization at all)
          blockSelector: '.ph-no-capture, .razorpay-checkout-frame, iframe[src*="razorpay"]',
          recordCrossOriginIframes: false,
        },

        // Disable replay entirely if not opted in, OR if user is not an operator
        disable_session_recording: !replayShouldBoot,

        // Privacy defaults
        respect_dnt: true,                       // honour Do-Not-Track header
        opt_out_capturing_by_default: false,    // only capture authenticated operator sessions
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: false,

        // Bootstrap
        loaded: (ph) => {
          _phInstance = ph;
          // Surface for the AnalyticsProvider polling path
          try { window.posthog = ph; } catch (_) { /* swallow */ }
          // Tag every session with the privacy mode for queryability
          try {
            ph.register({
              privacy_mode: replayShouldBoot ? 'replay_on' : 'replay_off',
              operator_role: role || 'none',
            });
          } catch (_) { /* swallow */ }
        },
      });

      return posthog;
    } catch (err) {
      // NEVER throw — analytics is best-effort.
      // eslint-disable-next-line no-console
      console.warn('[posthog_init] init failed (non-fatal):', err?.message);
      return null;
    }
  })();

  return _initPromise;
}


/**
 * Idempotent opt-out — operator can revoke their replay consent any time.
 * Wires to the Settings toggle (when shipped).
 */
export function optOutOfReplay() {
  try { _phInstance?.stopSessionRecording?.(); } catch (_) { /* swallow */ }
  try { _phInstance?.opt_out_capturing?.(); } catch (_) { /* swallow */ }
}

/**
 * Idempotent opt-in — used by the Settings toggle when the operator
 * explicitly enables replay.
 */
export function optInToReplay() {
  try { _phInstance?.opt_in_capturing?.(); } catch (_) { /* swallow */ }
  try { _phInstance?.startSessionRecording?.(); } catch (_) { /* swallow */ }
}

/**
 * Phase 261f — Identify the logged-in operator on PostHog so events flow into
 * per-user + per-academy dashboards. Idempotent: safe to call on every auth
 * state change. Best-effort: never throws, never blocks.
 *
 * What this does:
 *   1. posthog.identify(user.id, { ...person_properties })
 *      → Attaches role, academy_id, academy_name to the PostHog Person.
 *   2. posthog.group('academy', academy_id, { name: academy_name })
 *      → First-class PostHog Group Analytics so super admins can pivot the
 *        funnel by academy (e.g. "which 47 of 1001 academies installed").
 *   3. posthog.register({ academy_id, academy_name, persona })
 *      → Super properties auto-attached to EVERY subsequent event in this
 *        session so even raw event filters carry the context.
 *
 * Privacy:
 *   • We do NOT send raw email (we keep PostHog's existing mask doctrine).
 *   • We do NOT send the user's name (operator may not consent).
 *   • Only the academy-level + role-level attributes go up.
 */
const DESKTOP_PERSONAS = new Set(['academy_owner', 'coach', 'employee', 'super_admin']);

function _personaForRole(role) {
  return DESKTOP_PERSONAS.has((role || '').toLowerCase()) ? 'desktop' : 'mobile';
}

export function identifyOperator(user) {
  if (!user || !user.id) return;
  try {
    const ph = _phInstance || (typeof window !== 'undefined' ? window.posthog : null);
    if (!ph || typeof ph.identify !== 'function') return;
    const role = (user.role || '').toLowerCase();
    const academyId = user.academy_id || null;
    const academyName = user.academy_name || null;
    const persona = _personaForRole(role);

    // 1. Person properties
    ph.identify(user.id, {
      role,
      persona,
      academy_id: academyId,
      academy_name: academyName,
      academy_slug: user.academy_slug || null,
    });

    // 2. PostHog Group Analytics — first-class per-academy pivot.
    if (academyId && typeof ph.group === 'function') {
      ph.group('academy', academyId, {
        name: academyName || academyId,
      });
    }

    // 3. Super properties (attached to every event in this session)
    if (typeof ph.register === 'function') {
      ph.register({
        operator_role: role || 'none',
        persona,
        academy_id: academyId,
        academy_name: academyName,
      });
    }
  } catch (_) {
    /* analytics is best-effort */
  }
}

/**
 * Phase 261f — Reset PostHog identity on logout so a shared device
 * doesn't conflate two users' events under one distinct_id.
 *
 * Without this, after operator A logs out and operator B logs in on the
 * same browser, B's events would attribute to A's distinct_id until the
 * next identify() call lands.
 */
export function resetOperator() {
  try {
    const ph = _phInstance || (typeof window !== 'undefined' ? window.posthog : null);
    if (ph && typeof ph.reset === 'function') ph.reset();
  } catch (_) {
    /* analytics is best-effort */
  }
}



/**
 * Diagnostic — returns the current replay status for the Settings UI.
 */
export function getReplayStatus() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const sessionReplayEnabled =
    (process.env.REACT_APP_POSTHOG_SESSION_REPLAY || '').toLowerCase() === 'true';
  return {
    configured: !!key && key !== 'placeholder',
    replay_env_enabled: sessionReplayEnabled,
    currently_recording: !!(_phInstance && _phInstance.sessionRecordingStarted?.()),
    privacy_mode: sessionReplayEnabled ? 'replay_on' : 'replay_off',
  };
}
